import { useId, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Card, Box, Stack, Chip, Collapse, Typography, ButtonBase, Button } from '@mui/material';

// ----------------------------------------------------------------------
// Website journey — a read-only, collapsed-by-default timeline of the page
// views the backend correlated to this lead (`leadDetail.journey_events`).
//
// Privacy rules baked in here on purpose:
//   * click identifiers (gclid / gbraid / wbraid / gad_campaignid / fbclid)
//     are rendered as PRESENCE chips only, never as their raw values;
//   * landing/referrer URLs are rendered scheme+host+path only — query
//     strings routinely carry click ids, session tokens and search terms.
// ----------------------------------------------------------------------

const PAGES_PER_SESSION = 10;

const UNTRACKED_SESSION_LABEL = 'Untracked session';

// All events with a null/empty session_id are merged into a single group
// under this key, rather than one group per orphan event.
const UNTRACKED_KEY = '__untracked__';

const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'gad_campaignid'];
const META_CLICK_KEYS = ['fbclid'];

/**
 * Reduce a URL to scheme + host + path, dropping the query string and
 * fragment. Deliberately string-based so it never throws on the malformed
 * values that turn up in referrer headers.
 */
export const stripQuery = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url.split('#')[0].split('?')[0];
};

const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const timeOf = (event) => {
    const ms = new Date(event?.occurred_at).getTime();
    return Number.isNaN(ms) ? 0 : ms;
};

/** `google / cpc`, `google`, or `Direct` when there is no utm attribution. */
const touchLabel = (event) => {
    const source = event?.attribution?.utm_source;
    const medium = event?.attribution?.utm_medium;
    if (source && medium) return `${source} / ${medium}`;
    return source || medium || 'Direct';
};

const hasAnyKey = (events, keys) =>
    events.some((event) => keys.some((key) => Boolean(event?.attribution?.[key])));

/**
 * Group events by `session_id`, oldest page first inside each session. Every
 * event with a null/empty `session_id` is merged into ONE "Untracked
 * session" group (rather than one group per orphan event) so a lead with
 * many untracked page views doesn't flood the timeline with single-page
 * groups.
 *
 * Groups are ordered by their first event's `occurred_at` (most recent
 * first), except the untracked group, which always sorts LAST regardless of
 * its own timestamps — it's a catch-all bucket, not a real session, so it
 * shouldn't interleave with the chronological read of tracked sessions.
 */
const groupBySession = (events) => {
    const groups = new Map();
    events.forEach((event) => {
        const key = event?.session_id || UNTRACKED_KEY;
        if (!groups.has(key)) {
            groups.set(key, { key, sessionId: event?.session_id || null, events: [] });
        }
        groups.get(key).events.push(event);
    });

    return [...groups.values()]
        .map((group) => {
            const ordered = [...group.events].sort((a, b) => timeOf(a) - timeOf(b));
            return {
                ...group,
                events: ordered,
                startedAt: ordered[0]?.occurred_at,
                endedAt: ordered[ordered.length - 1]?.occurred_at,
            };
        })
        .sort((a, b) => {
            if (a.key === UNTRACKED_KEY) return 1;
            if (b.key === UNTRACKED_KEY) return -1;
            return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
        });
};

// ----------------------------------------------------------------------

const SessionGroup = ({ group, index }) => {
    const [showAll, setShowAll] = useState(false);

    const label = group.sessionId ? `Session ${index + 1}` : UNTRACKED_SESSION_LABEL;
    const visible = showAll ? group.events : group.events.slice(0, PAGES_PER_SESSION);
    const hidden = group.events.length - visible.length;
    const firstEvent = group.events[0];

    return (
        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}>
            <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
                <Typography variant='subtitle2'>{label}</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {`${formatDateTime(group.startedAt)} — ${formatDateTime(group.endedAt)}`}
                </Typography>
            </Stack>

            {stripQuery(firstEvent?.landing_url) && (
                <Stack direction='row' spacing={0.5} sx={{ mt: 0.5 }} flexWrap='wrap'>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        Landing page:
                    </Typography>
                    <Typography variant='caption'>{stripQuery(firstEvent?.landing_url)}</Typography>
                </Stack>
            )}
            {stripQuery(firstEvent?.referrer) && (
                <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        Referrer:
                    </Typography>
                    <Typography variant='caption'>{stripQuery(firstEvent?.referrer)}</Typography>
                </Stack>
            )}

            <Box
                component='ul'
                aria-label={`Pages viewed in ${label}`}
                sx={{ listStyle: 'none', m: '8px 0 0', p: 0 }}
            >
                {visible.map((event) => (
                    <Box
                        component='li'
                        key={event.id}
                        sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.25 }}
                    >
                        <Typography variant='caption' sx={{ color: 'text.secondary', minWidth: 48 }}>
                            {formatTime(event.occurred_at)}
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {event.page_path}
                        </Typography>
                        {event.page_title && (
                            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {event.page_title}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {hidden > 0 && (
                <Button size='small' onClick={() => setShowAll(true)} sx={{ mt: 0.5 }}>
                    {`Show more (${hidden})`}
                </Button>
            )}
        </Box>
    );
};

SessionGroup.propTypes = {
    group: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
};

// ----------------------------------------------------------------------

const JourneyHistory = ({ leadDetail }) => {
    const [expanded, setExpanded] = useState(false);
    // Unique per instance so two JourneyHistory cards on the same page (e.g.
    // side-by-side merge review) can't collide on the same DOM id.
    const contentId = useId();

    // Historical leads (pre-visitor-tracking) simply have no key at all.
    const events = leadDetail?.journey_events ?? [];

    // No data => no card, so the Overview layout is unchanged for those leads.
    if (!events.length) return null;

    const byTime = [...events].sort((a, b) => timeOf(a) - timeOf(b));
    const oldest = byTime[0];
    const newest = byTime[byTime.length - 1];

    const groups = groupBySession(events);
    const hasGoogleClickId = hasAnyKey(events, GOOGLE_CLICK_KEYS);
    const hasMetaClickId = hasAnyKey(events, META_CLICK_KEYS);

    return (
        <Card sx={{ p: 2, mt: 2 }}>
            <ButtonBase
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={contentId}
                sx={{ width: '100%', justifyContent: 'space-between', p: 0.5, borderRadius: '8px' }}
            >
                <Typography variant='subtitle1'>Website journey</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {`${events.length} ${events.length === 1 ? 'page view' : 'page views'}`}
                </Typography>
            </ButtonBase>

            <Collapse in={expanded} unmountOnExit id={contentId}>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Box>
                        <Typography variant='body2' data-testid='journey-first-touch'>
                            {`First touch: ${touchLabel(oldest)}`}
                        </Typography>
                        <Typography variant='body2' data-testid='journey-latest-touch'>
                            {`Latest touch: ${touchLabel(newest)}`}
                        </Typography>
                    </Box>

                    {(hasGoogleClickId || hasMetaClickId) && (
                        <Stack direction='row' spacing={1} flexWrap='wrap'>
                            {hasGoogleClickId && (
                                <Chip
                                    size='small'
                                    label='Google click ID'
                                    aria-label='A Google click ID was recorded for this lead'
                                />
                            )}
                            {hasMetaClickId && (
                                <Chip
                                    size='small'
                                    label='Meta click ID'
                                    aria-label='A Meta click ID was recorded for this lead'
                                />
                            )}
                        </Stack>
                    )}

                    {// Number only tracked sessions (Session 1, Session 2, …); the
                    // untracked group never consumes a number, so a tracked
                    // session's number can't be thrown off by where the
                    // untracked group happens to land.
                    (() => {
                        let trackedIndex = 0;
                        return groups.map((group) => {
                            const sessionIndex = group.sessionId ? trackedIndex : 0;
                            if (group.sessionId) trackedIndex += 1;
                            return <SessionGroup key={group.key} group={group} index={sessionIndex} />;
                        });
                    })()}
                </Stack>
            </Collapse>
        </Card>
    );
};

JourneyHistory.propTypes = {
    leadDetail: PropTypes.object,
};

JourneyHistory.defaultProps = {
    leadDetail: null,
};

export default JourneyHistory;
